import dayjs from "dayjs";
import { useGetAssetPositionAssetLinkListAssetPositionLinkGet } from "kubb";
import { useMemo } from "react";
import type { Asset } from "utils/object";

export interface AssetRetirementData {
  hasActiveLink: boolean;
  lastUninstallDate: Date | null;
  lastUsedInfo: {
    hasBeenUsed: boolean;
    lastUninstallDate: Date | null;
    formattedDate: string | null;
  };
  isLoading: boolean;
}

/**
 * Custom hook to manage asset retirement data and business logic.
 * Separates data fetching and computation from presentation.
 */
export function useAssetRetirement(asset: Asset): AssetRetirementData {
  // Load asset position links
  const { data: linksResponse, isLoading } =
    useGetAssetPositionAssetLinkListAssetPositionLinkGet({
      asset_id: asset.asset_id,
      company_id: asset.company_id,
    });

  const links = linksResponse?.data || [];

  // Check if asset has an active (non-cancelled, no uninstall date) link
  const hasActiveLink = useMemo(() => {
    return links.some((link) => !link.cancelled && !link.uninstall_date);
  }, [links]);

  // Find the most recent uninstalled link (not cancelled, has uninstall_date)
  const lastUninstallDate = useMemo(() => {
    const uninstalledLinks = links
      .filter((link) => !link.cancelled && link.uninstall_date)
      .sort((a, b) => {
        const dateA = dayjs(a.uninstall_date);
        const dateB = dayjs(b.uninstall_date);
        return dateB.diff(dateA); // Most recent first
      });

    return uninstalledLinks.length > 0
      ? dayjs(uninstalledLinks[0].uninstall_date).toDate()
      : null;
  }, [links]);

  // Calculate usage information for display
  const lastUsedInfo = useMemo(() => {
    const hasBeenUsed = links.some((link) => !link.cancelled);

    if (!hasBeenUsed) {
      return {
        hasBeenUsed: false,
        lastUninstallDate: null,
        formattedDate: null,
      };
    }

    if (lastUninstallDate) {
      return {
        hasBeenUsed: true,
        lastUninstallDate,
        formattedDate: `${dayjs(lastUninstallDate).format("dddd D MMMM YYYY")} 23:59:59`,
      };
    }

    // Asset has been used but is currently installed
    return {
      hasBeenUsed: true,
      lastUninstallDate: null,
      formattedDate: null,
    };
  }, [links, lastUninstallDate]);

  return {
    hasActiveLink,
    lastUninstallDate,
    lastUsedInfo,
    isLoading,
  };
}
